import React, { useContext, useMemo, useRef } from 'react';
import styled from 'styled-components';
import {
	DndContext,
	DragEndEvent,
	DragOverlay,
	DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useDispatch, useSelector } from 'react-redux';
import GRInput from '../../general-components/input/gr-input';
import DraggingGrPicture from '../../general-components/input/dragging-gr-picture';
import GrPictureInput from '../../general-components/input/gr-picture-input';
import useDragContent from '../../../hooks/content/use-drag-content';
import { FormContext } from '../../../contexts/generated-content/content-form-context';
import { setDraggingProps } from '../../../stores/actions/user-interface';
import { GreenroomState } from '../../../stores/types/types';
import { StylePackContentType, Field, InputTypeByCapsuleType, FieldType, HeaderText, headerTypes } from '../../types/stylepacks';
import PresenterDropdown from './presenter-dropdown';
import StylepackNoContentAlert from '../../general-components/alert-basic/wrappers/stylepack-no-content';
import { contentFilterOptions } from '../../style-packs/style-packs-tab';
import useMediaItemMemos from '../../../hooks/use-media-memos';
import SoundVersionManager from '../../../utils/sound/sound-manager';
import { ChipContext } from '../../../contexts/generated-content/content-chip-context';

const StyledForm = styled.form`
	z-index: $z-index-media-link;
    padding: 0px 24px;
    position: relative;
	flex: 1;
	overflow-y: scroll;
    padding: 0 8px 0 24px;

	.duration-input-container {
		max-height: 32px;
		margin-top: 19px !important;
		.duration-container {
			margin-bottom: 0;
		}
	}
`;

const FormContainer = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	height: 100%;
	overflow-x: hidden;
`;

const StyledDiv = styled.div`
	background-color: var(--primitives-mode-1-gray-900);
	border-radius: 12px;
	margin-top: 15px;
	& > div.header-section {
		margin: 12px 0 16px 0;
		&:first-of-type {
			margin-top: 24px;
		}
	}
	.animation-row {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 10px;
		height: 40px;
		& > label {
			width: 100px;
			margin-right: 10px;
			color: #908ca0;
		}
	}
	& > hr.header-line {
		margin-top: 24px;
		margin-bottom: 13px;
		border-color: rgba(144,140,160,.1);
	}
`;

const hrRules = (
	id: string, // this fields "identifier"
	i: number, // the total index of this field among all fields
	sectionId: string | undefined, // the identifier this field is grouped under
	prevId: string | undefined, // the previous field's identifier
	contentType: string | undefined, //title, bookend, etc
	templateName: string | undefined,
) => {
	let show = false;
	
	// first image not in a presenter info section has a hr before it
	if (id === 'image' 
		&& prevId !== 'image'
		&& sectionId !== HeaderText.PresenterInfo
	) show = true;

	// topo items have background image then logo and no divider in between
	if (sectionId?.toLowerCase() === 'logo' && templateName?.toLowerCase() === 'topo') {
		show = false;
	}

	// "duration" fields always have lines above
	// unless they are the only field (0th index)
	if (id === 'duration' && i !== 0)
		show = true;
	
	// "Intro Copy" fields always have lines below except bookends
	if (id === HeaderText.IntroCopy) 
		show = contentType !== StylePackContentType.BOOKEND;

	return show ? <hr className='header-line' /> : null;
}

// these fields are the first field in walkups/intros that the text is titled "intro copy" for
const introFieldNamesForWalkupCopy = [
	'Main Text','Company Name','Text 1', 'Text 2', 'Text 3', 'Text 4',
	'text-1', 'text-3' // topo walkup introducting/from text title
];
const fieldNamesForBookendIntroCopy = ['Main Text','Intro','Headline','Title 1','Title 2'];
const imgFieldTypes = ['picture','photo','image','asset'];
const shapePatternFields = ['shape','pattern'];


function walkupFieldIsIntroOrPresenterInfo(
	fieldName: string,
	stylepackName: string | undefined
): HeaderText {
	if (stylepackName === 'Rothko') {
		if (fieldName === 'Text 2' || fieldName === 'Text 3') {
			// in other packs these are "intro" fields
			return HeaderText.PresenterInfo;
		}
	}

	if (stylepackName === 'Topo' && fieldName === 'Text 2') {
		return HeaderText.PresenterInfo;
	}

	if (stylepackName === 'Mondrian' && fieldName === 'Text 5') {
		return HeaderText.PresenterInfo;
	}

	if (introFieldNamesForWalkupCopy.includes(fieldName)) {
		return HeaderText.IntroCopy;
	}

	return HeaderText.PresenterInfo;
}

type Props = {
	fieldTypes?: string[];
}

export default function ContentGeneratorHandler(props: Props) {
	const token = useSelector((s: GreenroomState) => s.SessionReducer.token);
	const meetingId = useSelector((s: GreenroomState) => s.SessionReducer.meeting?.meeting_id);

	const { fieldTypes } = props;
	const dispatch = useDispatch();

	const imageFieldCount = useRef(0);
	
	const {
		statefulFormValues,
		selectedGraphicApiShape,
		addNewPicture,
		removePictureWithCount,
		defaultTitleOverlayData,
		onDragEnd,
		onChange,
		uniqueId,
		currTemplate: currentTemplate,
		currentAudioMediaItem
	} = useContext(FormContext);

	const {shouldShowChipMenu} = useContext(ChipContext);

	const {
		draggingCount,
		draggingOver,
		handleDragEnd,
		handleDragOver,
		handleDragStart,
		sensors,
	} = useDragContent(onDragEnd, currentTemplate);

	const getGrPictureInput = (field: Field, count: number) => (
		<GrPictureInput
			key={`picture-${count}-${uniqueId}-${field.userData}-${field.name}-${field.type}`}
			onChange={onChange}
			addPicture={addNewPicture}
			removePicture={removePictureWithCount}
			values={statefulFormValues}
			defaultValues={defaultTitleOverlayData?.default}
			count={count}
			totalCount={Number(statefulFormValues?.pictureCount ?? 1)}
			draggingCount={draggingCount}
			draggingOver={draggingOver}
			field={field}
			isModal={true}
			autoSegment={field.removebg}
		/>
	);

	const getDragOverlay = (field: Field) => (
		<DraggingGrPicture
			key={`picture-${field.userData}-${uniqueId}`}
			fieldName={field.userData}
			options={field.options}
			variables={field?.variables ?? []}
			values={statefulFormValues}
			defaultValues={defaultTitleOverlayData?.default}
			count={draggingCount}
		/>
	);

	const pictureInputComponent = (field: Field, hideName: boolean) => {
		const count = Number(statefulFormValues?.pictureCount ?? 1);
		const pictures = [];
		const names = [];
		if (imageFieldCount.current < 2 && hideName) {
			field.name = '';
		}
		for (let i = 0; i < count; i += 1) {
			pictures.push(getGrPictureInput(field, i + 1));
			names.push(`picture-${i}`);
		}
		return (
			<DndContext
				key={`${field.userData}-${uniqueId}`}
				sensors={sensors}
				onDragMove={handleDragOver}
				onDragStart={(e: DragStartEvent) => {
					handleDragStart(e);
					dispatch(setDraggingProps({
						typeOfDragging: 'picture',
						idOfDragging: field.userData,
						globalIsDragging: true,
					}));
				}}
				onDragEnd={(e: DragEndEvent) => {
					handleDragEnd(e);
					dispatch(setDraggingProps(null));
				}}
				
			>
				{field.header && (
					<div className={`header-section bl-font-s1 text-gray-200`}>
						{field.header}
					</div>
				)}
				<SortableContext
					items={names}
					strategy={verticalListSortingStrategy}
				>
					{pictures}
				</SortableContext>
				{draggingCount > 0 && (
					<DragOverlay>
						{getDragOverlay(field)}
					</DragOverlay>
				)}
			</DndContext>
		);
	};

	const fieldsGroupedByType = useMemo(() => {
		const groups: { [type: string]: Field[] } = {};

		imageFieldCount.current = 0;
		selectedGraphicApiShape.forEach((field) => {
			const fieldType = field.type === "text" ? "string" : field.type;

			if (field.userData === 'shape' || field.userData === 'pattern') {
				// do not show shape selectors in UI
				return;
			}

			const isPicture = imgFieldTypes.includes(fieldType);
			if (fieldTypes && !fieldTypes?.includes(fieldType)) {
				return;
			} else if (isPicture) {
				imageFieldCount.current += 1;
			}


			let key = (
				field.name === "Tagline"
				&& currentTemplate?.name === "Rothko"
			 ) ? "Tagline" : headerTypes[fieldType];

			if (field.name === "Seconds") {
				field.name = "Minutes";
			}

			if (key === HeaderText.Copy) {
				if (currentTemplate?.contentType === StylePackContentType.TITLE) {
					key = HeaderText.PresenterInfo;
				} else if (currentTemplate?.contentType === StylePackContentType.WALKUP) {
					key = walkupFieldIsIntroOrPresenterInfo(field.name, currentTemplate?.name)
				} else if (currentTemplate?.contentType === StylePackContentType.BOOKEND) {
					if (isPicture) {
						key = field.name;
					} else {
						key = fieldNamesForBookendIntroCopy.includes(field.name)
							? HeaderText.IntroCopy
							: HeaderText.MainCopy;
					}
				}
			}

			if (groups[key]) {
				if (field.type === "duration") {
					groups[key].unshift(field);
				} else if (currentTemplate?.type === "api-brandlive-mondrian-walkup-01" && field.name === "Text 5") {
					groups[key].unshift(field);
				} else {
					groups[key].push(field);
				}
			} else {
				groups[key] = [field];
			}
		});

		const groupedFields = Object.entries(groups).sort((a,b) => {
			// sort presenter info to the top
			if (a[0] === HeaderText.PresenterInfo) return 1;
			if (b[0] === HeaderText.PresenterInfo) return -1;
			return 0;
		});
		return groupedFields;
	}, [selectedGraphicApiShape, currentTemplate?.contentType, currentTemplate]);

	function onGRInputChange(event: any) { //e: React.ChangeEvent<HTMLInputElement>) {
		//if a change in countdown type and there is a audio item
		if (currentTemplate.contentType == 'countdown' && event.target?.name == 'seconds' && token && currentAudioMediaItem?.provider) {
			let targetTime = parseInt(event.target?.value);
			let data = currentAudioMediaItem.media_data as any;
			if (targetTime && data?.original?.trackId) {
				const soundManager = SoundVersionManager.getInstance();
				soundManager.fetchVersions(token, data.original.trackId, targetTime, {}, meetingId, currentAudioMediaItem.id);
			}
		}

		//continue the default change handler
		onChange(event);
	}


	return (
		<FormContainer className='custom-scrollbar'>
			<StyledForm autoComplete="off" className='settings-container'>
				{fieldsGroupedByType.map(([type, fields], index, groups) => (
					<StyledDiv key={`${type}-${uniqueId}`}>
						{['duration', ...imgFieldTypes].includes(fields[0].type) ? null : <>
							<div className='header-section bl-font-s1'>{type}</div>
							<PresenterDropdown
								disabled={currentTemplate?.locked}
								shown={
									type === HeaderText.PresenterInfo 
									&& currentTemplate?.contentType === StylePackContentType.WALKUP 
									&& currentTemplate?.type !== "api-brandlive-mondrian-walkup-01"
								}
							/>
							{(type === HeaderText.Copy
								&& currentTemplate?.type === 'brandlive-walkups'
								&& Number(statefulFormValues.pictureCount ?? 0) > 0
							) ? <GRInput
								fieldName="eventName"
								friendlyName="Event Name"
								fieldType={'text'}
								value={statefulFormValues.eventName ?? 'Brandlive Studios'}
								onChange={onChange}
								options={[]}
								labelStyle='bl-font-s1 text-gray-200'
							/> : null}
						</>}
						{fields.map((ff: Field, ii: number) => (
							<React.Fragment key={`${ff.userData}-${uniqueId}-${ff.name}-${ff.type}`}>
								{hrRules(ff.type, ii, type, fields[ii - 1]?.type, currentTemplate?.contentType, currentTemplate?.name)}
								{InputTypeByCapsuleType[ff.type] === 'picture'
									? pictureInputComponent(ff, currentTemplate?.contentType === StylePackContentType.WALKUP)
									: <GRInput
										key={`input-${ff.userData}-${uniqueId}-${ff.name}-${ff.type}`}
										fieldName={ff.userData}
										friendlyName={ff.name}
										fieldType={InputTypeByCapsuleType[ff.type]}
										onChange={onGRInputChange}
										options={ff.options}
										value={statefulFormValues?.[ff.userData]}
										disabled={currentTemplate?.locked}
										labelStyle='bl-font-s1 text-gray-200'
										showChipMenu={shouldShowChipMenu(type === HeaderText.PresenterInfo)}
									/>
								}
								{currentTemplate?.type === "api-brandlive-mondrian-walkup-01" && ff.name === "Text 5" && (
									<div style={{margin: '24px 0 16px 0'}}>
										<PresenterDropdown
											disabled={currentTemplate?.locked}
											shown={type === HeaderText.PresenterInfo && currentTemplate?.contentType === StylePackContentType.WALKUP}
										/>
									</div>
								)}
							</React.Fragment>
						))}
						{!!fieldsGroupedByType[index + 1] ? hrRules(type, index, type, undefined, currentTemplate?.contentType, currentTemplate?.name) : null}
					</StyledDiv>
				))}
				<div style={{minHeight: '48px'}}/>
			</StyledForm>
		</FormContainer>
	);
}
